import React, { useState, useEffect, useRef } from "react";
import {
  Container,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Divider,
  Typography,
  Box,
  ToggleButtonGroup,
  ToggleButton,
  CircularProgress,
  Snackbar,
  Alert,
  Paper,
} from "@mui/material";
import { useMediaQuery } from "react-responsive";
import { useTranslation } from "react-i18next";
import { useSettingsManagement } from "../hooks/useSettingsManagement";
import MovementSettingsForm from "./components/MovementSettingsForm";
import ExerciseSettingsForm from "./components/ExerciseSettingsForm";
import TrainingsPlanIcon from "../assets/TrainingsPlanIcon.png";

/**
 * Refactored Settings Component
 *
 * A clean, focused component that:
 * - Uses custom hook for all business logic
 * - Uses service layer for API operations
 * - Focuses only on UI presentation and layout
 * - Breaks down complex forms into smaller components
 * - Provides better error handling and loading states
 *
 * @component
 * @param {Object} props - Component props
 * @param {string} props.ergonomoTokenFromMain - Auth token (legacy prop)
 * @param {string} props.ergonomoRefreshTokenFromMain - Refresh token (legacy prop)
 */
const SettingsComponent = () => {
  const { t } = useTranslation();
  const isMobile = useMediaQuery({ maxWidth: 767 });

  // Currently selected tab in the settings UI ('movement' | 'exercise')
  const [selectedTab, setSelectedTab] = useState("movement");

  // Ref for the inner scrollable content area. Used by reflow/resizing logic
  // to correctly show/hide scrollbars and avoid layout jumps when content
  // changes height (e.g. after changing tabs or loading remote data).
  const scrollableRef = useRef(null);

  /*
   * `useSettingsManagement` is the central hook that encapsulates all data
   * fetching, mutation and dialog-state logic for the settings screen.
   * It intentionally returns a flat object with clearly grouped responsibilities:
   * - Data (preferences)
   * - Loading flags and derived `isDataLoaded`
   * - Movement settings and update/save helpers
   * - Exercise settings and update/save helpers + initialization
   * - Dialog state booleans and close handlers
   * - Notification/snackbar state and error handling helpers
   * - Utility flags (e.g. `hasUnsavedChanges`)
   *
   * The component remains presentational: all side-effects and API calls
   * happen inside the hook.
   */
  const {
    // Data
    preferences,

    // Loading states
    isLoading,
    isDataLoaded,

    // Movement settings
    movementSettings,
    updateMovementSetting,
    toggleMovementDay,
    saveMovementSettings,

    // Exercise settings
    exerciseSettings,
    updateExerciseSetting,
    toggleExerciseDay,
    saveExerciseSettings,
    initializeExerciseSetting,
    snoozeExercise,

    // Dialog states
    movementSnoozeDialogOpen,
    exerciseSnoozeDialogOpen,
    closeMovementSnoozeDialog,
    closeExerciseSnoozeDialog,

    // Notification states
    snackbarOpen,
    closeSnackbar,
    error,
    clearError,

    // Utilities
    hasUnsavedChanges,
  } = useSettingsManagement();

  /**
   * Handles tab selection change
   */
  const handleTabChange = (event, newValue) => {
    if (newValue) {
      setSelectedTab(newValue);
    }
  };

  /**
   * Force layout recalculation when tab changes
   */
  useEffect(() => {
    /*
     * Small heuristic to force a reflow and ensure the inner scrollable
     * container correctly shows or hides its vertical scrollbar after
     * switching tabs or after the data has been loaded.
     *
     * We briefly toggle overflow to trigger layout recalculation and then
     * restore the desired overflow behaviour. The extra setTimeout calls
     * handle cases where content is rendered asynchronously (images,
     * remote text) and may change height after initial render.
     */
    const forceScrollRecalculation = () => {
      if (scrollableRef.current) {
        const element = scrollableRef.current;

        // Force a reflow by temporarily changing styles
        element.style.overflow = "hidden";
        // Force reflow by accessing offsetHeight
        void element.offsetHeight;
        element.style.overflowY = "auto";
        element.style.overflowX = "hidden";

        // Additional check after a brief delay for dynamic content
        setTimeout(() => {
          if (element.scrollHeight > element.clientHeight) {
            element.style.overflowY = "auto";
          }
        }, 100);
      }
    };

    // Initial calculation
    setTimeout(forceScrollRecalculation, 50);

    // Additional calculation after a longer delay for dynamic content
    setTimeout(forceScrollRecalculation, 200);
  }, [selectedTab, isDataLoaded]);

  /**
   * ResizeObserver to handle dynamic content changes
   */
  useEffect(() => {
    if (!scrollableRef.current) return;

    let frame = null;

    /*
     * Observe size changes inside the scrollable content area and update
     * the overflow behaviour on the next animation frame. This avoids
     * layout thrashing and keeps the scrollbar visible only when needed.
     */
    const resizeObserver = new ResizeObserver(() => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const el = scrollableRef.current;
        if (!el) return;
        el.style.overflowY =
          el.scrollHeight > el.clientHeight ? "auto" : "hidden";
      });
    });

    resizeObserver.observe(scrollableRef.current);

    return () => {
      cancelAnimationFrame(frame);
      resizeObserver.disconnect();
    };
  }, []);

  /**
   * Renders the header section
   */
  const renderHeader = () =>
    !isMobile && (
      <>
        <Grid item xs={12} md={5}>
          <img
            src={TrainingsPlanIcon}
            style={{
              width: "124px",
              height: "124px",
              marginLeft: "-24px",
            }}
            alt="Settings Icon"
          />
          <Typography
            variant="h4"
            style={{
              marginTop: "42px",
              color: "#063bd0",
            }}
            gutterBottom
          >
            <b>{t("trainingPlanHeading")}</b>
          </Typography>
          <Typography
            variant="h6"
            style={{
              marginTop: "18px",
              marginBottom: "42px",
              color: "#063bd0",
            }}
          >
            {t("trainingPlanSubheading")}
          </Typography>
        </Grid>

        <Grid item xs={12} md={1}>
          <Divider
            orientation="vertical"
            sx={{
              width: "2px",
              height: "100%",
              bgcolor: "divider",
            }}
          />
        </Grid>
      </>
    );

  /**
   * Renders the main content area
   */
  const renderMainContent = () => (
    <Grid
      item
      xs={12}
      md={isMobile ? 12 : 6}
      sx={{
        maxWidth: "100%",
        px: isMobile ? 0 : 1,
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
      }}
    >
      {!isDataLoaded ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "200px",
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <Box
          sx={{
            width: "100%",
            maxWidth: "100%",
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
            flexGrow: 1,
          }}
        >
          {/* Tab Selection */}
          <Paper
            elevation={1}
            sx={{
              p: isMobile ? 0.5 : 1,
              mb: isMobile ? 2 : 3,
              maxWidth: "100%",
              flexShrink: 0, // Don't shrink the tab selector
            }}
            className="settingsDivider"
          >
            <ToggleButtonGroup
              value={selectedTab}
              exclusive
              onChange={handleTabChange}
              fullWidth
              orientation={isMobile ? "vertical" : "horizontal"}
              sx={{
                maxWidth: "100%",
                overflow: "hidden",
                "& .MuiToggleButton-root": {
                  textTransform: "none",
                  fontWeight: 600,
                  py: isMobile ? 1 : 1.5,
                  px: isMobile ? 1 : 2,
                  fontSize: isMobile ? "0.875rem" : "1rem",
                  minWidth: isMobile ? "auto" : "120px",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                },
              }}
            >
              <ToggleButton value="movement">
                {t("movementReminders")}
              </ToggleButton>
              <ToggleButton value="exercise" className="exerciseToggleButton">
                {t("exerciseReminders")}
              </ToggleButton>
            </ToggleButtonGroup>
          </Paper>

          {/* Scrollable Content Area */}
          <Box
            ref={scrollableRef}
            sx={{
              flexGrow: 1,
              overflowY: "auto",
              overflowX: "hidden",
              minHeight: 0,
              maxHeight: isMobile ? "calc(92vh - 200px)" : "calc(74vh - 200px)", // Explicit max height
              paddingRight: isMobile ? 0 : 1, // Space for scrollbar
              paddingBottom: 2, // Bottom padding for comfortable scrolling
              "&::-webkit-scrollbar": {
                width: "6px",
              },
              "&::-webkit-scrollbar-track": {
                background: "rgba(0,0,0,0.1)",
                borderRadius: "3px",
              },
              "&::-webkit-scrollbar-thumb": {
                background: "rgba(0,0,0,0.3)",
                borderRadius: "3px",
                "&:hover": {
                  background: "rgba(0,0,0,0.5)",
                },
              },
            }}
          >
            {/* Unsaved Changes Warning */}
            {hasUnsavedChanges && (
              <Alert severity="warning" sx={{ mb: 2, mx: isMobile ? 0 : 1 }}>
                {t("unsavedChangesWarning")}
              </Alert>
            )}

            {/* Settings Forms */}
            {selectedTab === "movement" ? (
              <MovementSettingsForm
                settings={movementSettings}
                onUpdateSetting={updateMovementSetting}
                onToggleDay={toggleMovementDay}
                onSave={saveMovementSettings}
                disabled={isLoading}
              />
            ) : (
              <ExerciseSettingsForm
                settings={exerciseSettings}
                preferences={preferences}
                onUpdateSetting={updateExerciseSetting}
                onToggleDay={toggleExerciseDay}
                onInitialize={initializeExerciseSetting}
                onSave={saveExerciseSettings}
                disabled={isLoading}
              />
            )}
          </Box>
        </Box>
      )}
    </Grid>
  );

  return (
    <Container
      maxWidth="lg"
      sx={{
        height: isMobile ? "92vh" : "74vh",
        marginTop: isMobile ? "24px" : "0px",
        overflowX: "hidden",
        px: isMobile ? 1 : 3,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        className={isMobile ? "white-background-mobile" : "white-background"}
        sx={{
          width: "100%",
          flexGrow: 1,
          display: "flex",
          flexDirection: "column",
          borderRadius: isMobile ? 1 : 2,
          p: isMobile ? 1 : 2,
          minHeight: 0, // Important for flex children to shrink
          overflow: "hidden",
        }}
      >
        <Grid
          container
          spacing={isMobile ? 1 : 2}
          sx={{
            justifyContent: "center",
            alignContent: "flex-start",
            flexGrow: 1,
            overflowY: "auto",
            overflowX: "hidden",
            m: 0,
            width: "100%",
            minHeight: 0, // Important for scrolling
            "& .MuiGrid-item": {
              pl: isMobile ? 1 : 2,
              pt: isMobile ? 1 : 2,
            },
          }}
        >
          {renderHeader()}
          {renderMainContent()}
        </Grid>

        {/* Movement Snooze Confirmation Dialog */}
        <Dialog
          open={movementSnoozeDialogOpen}
          onClose={closeMovementSnoozeDialog}
          aria-labelledby="movement-snooze-dialog-title"
        >
          <DialogTitle id="movement-snooze-dialog-title">
            {t("movementSnoozeConfirmation")}
          </DialogTitle>
          <DialogContent sx={{ pb: 2 }}>
            <DialogContentText>
              {t("movementSnoozeConfirmationMessage")}
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={closeMovementSnoozeDialog} color="inherit">
              {t("cancel")}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Exercise Snooze Confirmation Dialog */}
        <Dialog
          open={exerciseSnoozeDialogOpen}
          onClose={closeExerciseSnoozeDialog}
          aria-labelledby="exercise-snooze-dialog-title"
        >
          <DialogTitle id="exercise-snooze-dialog-title">
            {t("exerciseSnoozeConfirmation")}
          </DialogTitle>
          <DialogContent sx={{ pb: 2 }}>
            <DialogContentText>
              {t("exerciseSnoozeConfirmationMessage")}
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={closeExerciseSnoozeDialog} color="inherit">
              {t("cancel")}
            </Button>
            <Button
              onClick={snoozeExercise}
              color="primary"
              variant="contained"
            >
              {t("proceed")}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Loading Dialog */}
        <Dialog
          open={isLoading}
          PaperProps={{
            style: {
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "20px",
              gap: "10px",
            },
          }}
        >
          <CircularProgress size={24} />
          <Typography>{t("loading")}</Typography>
        </Dialog>

        {/* Success Snackbar */}
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={6000}
          onClose={closeSnackbar}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert onClose={closeSnackbar} severity="success" variant="filled">
            {t("settingsSavedSuccessfully")}
          </Alert>
        </Snackbar>

        {/* Error Snackbar */}
        <Snackbar
          open={!!error}
          autoHideDuration={8000}
          onClose={clearError}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert onClose={clearError} severity="error" variant="filled">
            {error || t("settingsErrorOccurred")}
          </Alert>
        </Snackbar>
      </Box>
    </Container>
  );
};

export default SettingsComponent;
